<template>
  <div class="term-tag-input">
    <div class="term-tag-input__field" @click="focusInput">
      <sp-chip
        v-for="(term, index) in terms"
        :key="term"
        closable
        :label="term"
        @close="emit('remove-term', index)"
      ></sp-chip>
      <input
        ref="tagInputEl"
        class="term-tag-input__tag-field"
        :data-testid="TestId.assignSpecialties.termInput"
        :value="inputText"
        :placeholder="placeholder"
        :aria-labelledby="ariaLabelledbyAttr"
        @input="handleInput"
        @keydown.enter="handleCommitKey"
        @keydown="handleDelimiterKey"
        @paste="handleTagPaste"
        @blur="emit('commit')"
      />
    </div>

    <span class="term-tag-input__tag-count">
      {{ termCountText }}
    </span>
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { TestId } from "#/test-ids";
import { TERM_DELIMITER } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/utils/term-parser";

const emit = defineEmits(["update-input-text", "remove-term", "commit"]);

const props = defineProps({
  terms: {
    type: Array,
    default: () => [],
  },
  inputText: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  termCountText: {
    type: String,
    default: "",
  },
  // Id of the visible label element naming the tag input (declared as a prop so
  // it lands on the inner <input>, not the root div via attribute fallthrough).
  ariaLabelledby: {
    type: String,
    default: "",
  },
});

// Bind null (not "") so an unset prop renders no aria-labelledby attribute.
const ariaLabelledbyAttr = computed(() => props.ariaLabelledby || null);

const tagInputEl = useTemplateRef("tagInputEl");

function focusInput() {
  tagInputEl.value?.focus();
}

function handleInput(event) {
  emit("update-input-text", event.target.value);
}

function handleCommitKey(event) {
  const text = event.target.value.trim();

  if (text) {
    event.preventDefault();
    emit("commit");
  }
}

function handleDelimiterKey(event) {
  if (event.key === TERM_DELIMITER) {
    event.preventDefault();
    emit("commit");
  }
}

function handleTagPaste(event) {
  const pasted = event.clipboardData?.getData("text") || "";

  if (!pasted.includes(TERM_DELIMITER)) {
    return;
  }

  event.preventDefault();

  // Join the uncommitted typed text and the pasted text with the delimiter —
  // concatenating bare would fuse "Knee Pain" + "Hip Pain | …" into "Knee PainHip Pain".
  const existing = event.target.value;
  const combined = existing.trim() ? `${existing}${TERM_DELIMITER}${pasted}` : pasted;

  emit("commit", combined);
}
</script>

<style scoped lang="scss">
.term-tag-input {
  &__field {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: flex-start;
    gap: var(--sp-ref-spacing-2);
    min-height: 6rem;
    max-height: 12rem;
    overflow-y: auto;
    padding: var(--sp-ref-spacing-4);
    border: var(--sp-ref-spacing-px) solid var(--sp-sys-color-outline, #ccc);
    border-radius: var(--sp-ref-spacing-2);
    box-sizing: border-box;
    cursor: text;

    &:focus-within {
      outline: var(--sp-ref-spacing-1) solid var(--sp-sys-color-primary, #1976d2);
      outline-offset: calc(var(--sp-ref-spacing-px) * -1);
    }
  }

  &__tag-field {
    flex: 1 1 12rem;
    min-width: 8rem;
    border: none;
    outline: none;
    padding: var(--sp-ref-spacing-1) 0;
    font-family: var(--sp-sys-typescale-body-font-family, inherit);
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    line-height: 1.25rem;
    background: transparent;

    &::placeholder {
      color: var(--sp-sys-color-on-surface-variant, #666);
      font-style: italic;
      opacity: 0.6;
    }
  }

  &__tag-count {
    display: block;
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    line-height: 1.25rem;
    min-height: 1.25rem;
    color: var(--sp-sys-color-on-surface-variant, #666);
    margin-top: var(--sp-ref-spacing-2);
  }
}
</style>
